import { forwardRef } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cx, hasWidthClass } from './cx.ts';

/**
 * Text input / textarea with the Dispatch Deck field chrome: a recessed
 * `well` box with a hairline border that warms on hover and switches to
 * the signal accent on focus. Replaces the legacy bottom-border-only
 * fields (which read as empty voids on dark surfaces).
 *
 * Width defaults to `w-full` (forms), but a `w-*` in `className` wins —
 * see `hasWidthClass`; a hardcoded base `w-full` would otherwise
 * override a caller's fixed width via Tailwind source order.
 */
const FIELD_CLASSES =
  'rounded border border-line bg-well px-3 text-body text-paper placeholder:text-paper-faint transition-colors duration-120 hover:border-line-strong focus:border-signal-deep focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/20 disabled:pointer-events-none disabled:opacity-60';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly mono?: boolean;
  readonly invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { mono = false, invalid = false, className, ...rest },
  ref,
): JSX.Element {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(
        FIELD_CLASSES,
        'h-9',
        !hasWidthClass(className) && 'w-full',
        mono && 'font-mono',
        invalid && 'border-bad-line',
        className,
      )}
      {...rest}
    />
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly mono?: boolean;
  readonly invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { mono = false, invalid = false, className, rows, ...rest },
  ref,
): JSX.Element {
  return (
    <textarea
      ref={ref}
      rows={rows ?? 4}
      aria-invalid={invalid || undefined}
      className={cx(
        FIELD_CLASSES,
        'py-2',
        !hasWidthClass(className) && 'w-full',
        mono && 'font-mono',
        invalid && 'border-bad-line',
        className,
      )}
      {...rest}
    />
  );
});
